// Custom Fields Manager Modal:
// Create, edit, and delete tenant custom field definitions (text, number, date, boolean, select)
// with options management and cascading deletion confirmation.

import { useCallback, useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import {
  customFieldSave,
  customFieldDelete,
  type CustomFieldDoc,
  type CustomFieldType,
} from '../../lib/crm';

export interface CustomFieldsManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customFields: CustomFieldDoc[];
}

export default function CustomFieldsManagerModal({
  isOpen,
  onClose,
  customFields,
}: CustomFieldsManagerModalProps) {
  const { t } = useI18n();

  const [editingField, setEditingField] = useState<CustomFieldDoc | null>(null);
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<CustomFieldType>('text');
  const [optionsStr, setOptionsStr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(isOpen, panelRef, onClose);

  const confirmRef = useRef<HTMLDivElement>(null);
  const confirmTitleId = useId();
  const closeConfirm = useCallback(() => setDeleteId(null), []);
  useDialogA11y(isOpen && deleteId !== null, confirmRef, closeConfirm);

  if (!isOpen) return null;

  const keyValid = /^[a-z0-9_]{1,64}$/.test(key.trim());

  function startEdit(cf: CustomFieldDoc) {
    setEditingField(cf);
    setKey(cf.key);
    setLabel(cf.label || cf.key);
    setType(cf.type);
    setOptionsStr(cf.options ? cf.options.join(', ') : '');
    setError(null);
  }

  function resetForm() {
    setEditingField(null);
    setKey('');
    setLabel('');
    setType('text');
    setOptionsStr('');
    setError(null);
  }

  async function handleSave() {
    if (!keyValid) {
      setError('Key must be lowercase letters, numbers, and underscores (e.g. order_id)');
      return;
    }
    const trimmedLabel = label.trim() || key.trim();
    const options =
      type === 'select'
        ? optionsStr
            .split(/[,;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined;

    setBusy(true);
    setError(null);
    try {
      await customFieldSave({
        id: editingField ? editingField.id : undefined,
        key: key.trim().toLowerCase(),
        label: trimmedLabel,
        type,
        options,
      });
      resetForm();
    } catch (err) {
      setError((err as Error).message || 'Failed to save custom field');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    setBusy(true);
    setError(null);
    try {
      await customFieldDelete({ id });
      setDeleteId(null);
      if (editingField?.id === id) resetForm();
    } catch (err) {
      setError((err as Error).message || 'Failed to delete custom field');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 id={titleId} className="text-base font-bold text-slate-900">
              {t('subs.fields.title') || 'Manage Custom Fields'}
            </h3>
            <p className="text-xs text-slate-500">
              {t('subs.fields.subtitle') || 'Store custom attributes for subscribers, templates, and targeting'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Create / Edit Form */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {editingField
                ? t('subs.fields.edit') || 'Edit Custom Field'
                : t('subs.fields.create') || 'Create Custom Field'}
            </h4>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {t('subs.fields.key') || 'Field Key'}
                  </label>
                  <input
                    type="text"
                    value={key}
                    disabled={!!editingField}
                    placeholder={t('subs.fieldNamePlaceholder') || 'e.g. loyalty_points'}
                    onChange={(e) => setKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-mono outline-none focus:border-indigo-500 disabled:opacity-60"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Identifier in templates: <code>{'{{custom:key}}'}</code>
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {t('subs.fields.label') || 'Display Label'}
                  </label>
                  <input
                    type="text"
                    value={label}
                    placeholder={t('subs.fieldLabelPlaceholder') || 'e.g. Loyalty Points'}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {t('subs.fields.type') || 'Data Type'}
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as CustomFieldType)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                >
                  <option value="text">Text (String)</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean (Yes / No)</option>
                  <option value="select">Dropdown Select</option>
                </select>
              </div>

              {type === 'select' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Select Options (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={optionsStr}
                    placeholder="Bronze, Silver, Gold, Platinum"
                    onChange={(e) => setOptionsStr(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              )}

              {error && <p className="text-xs font-medium text-red-600">⚠️ {error}</p>}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || !key.trim()}
                  className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Saving...' : editingField ? 'Update Field' : 'Save Field'}
                </button>
                {editingField && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Existing Fields */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {t('subs.fields.list') || 'Configured Fields'} ({customFields.length})
            </h4>

            {customFields.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">
                No custom fields defined yet.
              </p>
            ) : (
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                {customFields.map((cf) => (
                  <div
                    key={cf.id}
                    className="flex items-center justify-between p-3 text-xs hover:bg-slate-50/80"
                  >
                    <div className="min-w-0 flex-1 pe-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">
                          {cf.label || cf.key}
                        </span>
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 font-mono">
                          {cf.key}
                        </code>
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 uppercase">
                          {cf.type}
                        </span>
                      </div>
                      {cf.options && cf.options.length > 0 && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          Options: {cf.options.join(', ')}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(cf)}
                        className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteId(cf.id)}
                        className="rounded px-2 py-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal Overlay */}
        {deleteId && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/40 p-4">
            <div
              ref={confirmRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={confirmTitleId}
              className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl space-y-3"
            >
              <h4 id={confirmTitleId} className="text-sm font-bold text-slate-900">Delete Custom Field?</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Deleting this custom field will automatically remove its values from all matching subscribers and from future import templates.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDeleteId(null)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(deleteId)}
                  disabled={busy}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700"
                >
                  {busy ? 'Deleting...' : 'Delete Field'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
